import { createSlice } from "@reduxjs/toolkit";
import { RootState } from "../../store";
import { toast } from "react-toastify";
import { fetchShipFromPorts, createShipFromPort, updateShipFromPort, deleteShipFromPort } from "./ShipFromPortAsyncThunk";
import { ShipFromPort } from "../../../types";
import { RequestResults } from "../../common/RequestStatus";

interface ShipFromPortState {
  data: ShipFromPort[];
  status: RequestResults;
  loading: boolean;
  error: string | null;
}

const initialState: ShipFromPortState = {
  data: [],
  status: RequestResults.idle,
  loading: false,
  error: null,
};

// Slice
const ShipFromPortSlice = createSlice({
  name: "ShipFromPort",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Fetch ShipFromPorts
    builder
      .addCase(fetchShipFromPorts.pending, (state) => {
        state.status = RequestResults.pending;
        state.error = null;
      })
      .addCase(fetchShipFromPorts.fulfilled, (state, action) => {
        state.status = RequestResults.success;
        state.data = action.payload;
      })
      .addCase(fetchShipFromPorts.rejected, (state, action) => {
        state.status = RequestResults.failed;
        toast.error(action.payload || "Failed to fetch ShipFromPorts");
        state.error = action.payload || "Failed to fetch ShipFromPorts";
      });

    // Add ShipFromPort
    builder
      .addCase(createShipFromPort.pending, (state) => {
        state.loading = true;
      })
      .addCase(createShipFromPort.fulfilled, (state, action) => {
        state.loading = false;
        state.data.push(action.payload);
      })
      .addCase(createShipFromPort.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload || "Failed to add ship from port");
        state.error = action.payload || "Failed to add ship from port";
      });

    // Update ShipFromPort
    builder
      .addCase(updateShipFromPort.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateShipFromPort.fulfilled, (state, action) => {
        const index = state.data.findIndex((item) => item.id === action.payload.id);
        if (index !== -1) {
          state.data[index] = action.payload;
        }
        state.loading = false;
      })
      .addCase(updateShipFromPort.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to update ship from port";
      });

    // Delete ShipFromPort
    builder
      .addCase(deleteShipFromPort.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteShipFromPort.fulfilled, (state, action) => {
        const index = state.data.findIndex((item) => item.id === action.payload.id);
        if (index !== -1) {
          state.data = state.data.filter((item) => item.id !== action.payload.id);
        }
        state.loading = false;
      })
      .addCase(deleteShipFromPort.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to delete ship from port";
      });
  },
});

export const selectShipFromPort = (state: RootState) => state.shipFromPort;

export default ShipFromPortSlice.reducer;
